import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { dealDotColors, HUE_KNOTS } from './dealGraphHue'

const SIZE_SAMPLES = [8, 14, 20]

const RAMP = `linear-gradient(to right, ${HUE_KNOTS.map(
  ({ ratio }) => dealDotColors(ratio).fill
).join(', ')})`

const Bar = styled.div`
  ${Typography.Body4}
  align-items: center;
  background: ${Colors.Semantic.Neutral.light_grey};
  border-bottom: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.75rem;
  padding: 0.75rem 1.5rem;

  @media ${Breakpoints.max.small} {
    gap: 0.5rem 1rem;
    padding: 0.625rem 1rem;
  }
`

const Group = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`

const Key = styled.span`
  color: ${Colors.Semantic.Neutral.black};
  font-weight: 700;
`

const Ramp = styled.span`
  background: ${RAMP};
  border-radius: 0.25rem;
  height: 0.5rem;
  width: 7.5rem;

  @media ${Breakpoints.max.small} {
    width: 4.5rem;
  }
`

const Circle = styled.span<{ $size: number }>`
  border: 1.5px solid ${Colors.Semantic.Neutral.dark_grey};
  border-radius: 50%;
  height: ${(o) => o.$size}px;
  width: ${(o) => o.$size}px;
`

const Full = styled.span`
  align-items: center;
  display: flex;
  gap: 0.5rem;

  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const Compact = styled.span`
  @media ${Breakpoints.min.medium} {
    display: none;
  }
`

const Hint = styled(Full)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin-left: auto;
`

const NoWrap = styled.span`
  white-space: nowrap;
`

const bold = (chunks: React.ReactNode) => <Key>{chunks}</Key>
const nowrap = (chunks: React.ReactNode) => <NoWrap>{chunks}</NoWrap>

/**
 * How to read the plot: what a dot's colour, size and height mean. A compact
 * wording on phones, where the bar wraps above a much narrower plot.
 */
export const DealGraphLegend = () => (
  <Bar>
    <Group>
      <Key>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.color" />
      </Key>
      <Full>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.colorBetter" />
      </Full>
      <Compact>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.colorBetterCompact" />
      </Compact>
      <Ramp />
      <Full>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.colorWorse" />
      </Full>
      <Compact>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.colorWorseCompact" />
      </Compact>
    </Group>
    <Group>
      <Key>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.size" />
      </Key>
      <Full>
        {SIZE_SAMPLES.map((size) => (
          <Circle $size={size} key={size} />
        ))}
        <FormattedMessage id="Listings.ListView.dealGraph.legend.sizeLarger" />
      </Full>
      <Compact>
        <FormattedMessage id="Listings.ListView.dealGraph.legend.sizeLargerCompact" />
      </Compact>
    </Group>
    <Full>
      <span>
        <FormattedMessage
          id="Listings.ListView.dealGraph.legend.lower"
          values={{ b: bold }}
        />
      </span>
    </Full>
    <Compact>
      <FormattedMessage
        id="Listings.ListView.dealGraph.legend.lowerCompact"
        values={{ b: bold, nowrap }}
      />
    </Compact>
    <Hint>
      <FormattedMessage id="Listings.ListView.dealGraph.legend.hover" />
    </Hint>
  </Bar>
)
